import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { getMainApi, getStoredToken, useAuthStore, useLocationStore } from '@elva/platform';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** When true, do not merge location_id into JSON body (budget section allowlist). */
    skipLocationIdBody?: boolean;
  }
}
/** Host only — axios baseURL (paths include `/v1/marketing/…` via `http.ts`). */
export const MARKETING_API_HOST = 'https://marketing-api.elva.ai';

/** Canonical API root from the handover doc. */
export const MARKETING_API_BASE = `${MARKETING_API_HOST}/v1/marketing/`;

/** Growth/marketing backend — separate from core-api-az (locations, org, etc.). */
export function getMarketingApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_MARKETING_API_BASE_URL as string | undefined;
  if (fromEnv !== undefined && fromEnv !== '') {
    return fromEnv.endsWith('/') ? fromEnv : `${fromEnv}/`;
  }
  // Dev default: same-origin + vite proxy (see vite.config.ts → marketing-api.elva.ai).
  // Override with VITE_MARKETING_API_BASE_URL=https://marketing-api.elva.ai/ for direct calls.
  return import.meta.env.PROD ? `${MARKETING_API_HOST}/` : '';
}

type MarketingRequestConfig = InternalAxiosRequestConfig;

function shouldSkipLocationIdBody(config: InternalAxiosRequestConfig): boolean {
  return Boolean(
    (config as MarketingRequestConfig & { skipLocationIdBody?: boolean }).skipLocationIdBody
  );
}

function injectLocationId(config: InternalAxiosRequestConfig, locationId: number): void {
  const existing =
    config.params && typeof config.params === 'object' && !Array.isArray(config.params)
      ? (config.params as Record<string, unknown>)
      : {};
  config.params = { ...existing, location_id: locationId };

  const method = (config.method ?? 'get').toLowerCase();
  if (method !== 'post' && method !== 'patch' && method !== 'put') return;
  if (shouldSkipLocationIdBody(config)) return;

  // Most mutations: live API reads location_id from request body, not query.
  const data = config.data;
  if (data == null || data === '') {
    config.data = { location_id: locationId };
    return;
  }
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    if (!data.has('location_id')) data.append('location_id', String(locationId));
    return;
  }
  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const obj = parsed as Record<string, unknown>;
        if (obj.location_id == null) {
          config.data = JSON.stringify({ ...obj, location_id: locationId });
        }
      }
    } catch {
      /* leave raw string body alone */
    }
    return;
  }
  if (typeof data === 'object' && !Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    if (obj.location_id == null) {
      config.data = { ...obj, location_id: locationId };
    }
  }
}

function attachBearer(config: InternalAxiosRequestConfig): void {
  let auth = getMainApi().defaults.headers.common.Authorization;
  if (!auth || typeof auth !== 'string') {
    const stored = getStoredToken();
    if (stored) auth = `Bearer ${stored}`;
  }
  if (auth && typeof auth === 'string') {
    config.headers.Authorization = auth;
  }
}

let marketingApi: AxiosInstance | null = null;
let isRedirectingToLogin = false;

async function redirectToAccountOnUnauthorized(): Promise<void> {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  // Clears token + hard-navigates to account login (same path as core/account clients).
  await useAuthStore.getState().logout();
}

export function getMarketingApi(): AxiosInstance {
  if (!marketingApi) {
    marketingApi = axios.create({
      baseURL: getMarketingApiBaseUrl(),
      timeout: 30_000,
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    });

    marketingApi.interceptors.request.use((config) => {
      attachBearer(config);

      const locationId = useLocationStore.getState().getSelectedCoreLocationId();
      if (locationId != null) {
        injectLocationId(config, locationId);
      }

      return config;
    });

    marketingApi.interceptors.response.use(
      (res) => res,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          await redirectToAccountOnUnauthorized();
        }
        return Promise.reject(error);
      }
    );
  }
  return marketingApi;
}
